import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

import Index from "./pages/Index";
import AdminBadge from "./components/AdminBadge";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import { SessionExpiryModal } from "./components/SessionExpiryModal";

// Lazy load non-critical routes
const Conta = lazy(() => import("./pages/Conta"));
const Biblioteca = lazy(() => import("./pages/Biblioteca"));
const MeusDocumentos = lazy(() => import("./pages/MeusDocumentos"));
const TemplatePage = lazy(() => import("./pages/TemplatePage"));
const DocumentoPage = lazy(() => import("./pages/DocumentoPage"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminRoute = lazy(() => import("./components/AdminRoute"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaDePrivacidade = lazy(() => import("./pages/PoliticaDePrivacidade"));
const LocalizaPreve = lazy(() => import("./pages/LocalizaPreve"));

const queryClient = new QueryClient();

const PageTransition = ({ children }: { children: React.ReactNode }) => (
  <div className="animate-page-enter">
    {children}
  </div>
);

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const AppRoutes = () => (
  <PageTransition>
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/biblioteca" element={<Biblioteca />} />
        <Route path="/template/:type" element={<TemplatePage />} />
        <Route path="/documento/:id" element={<DocumentoPage />} />
        <Route path="/meus-documentos" element={<MeusDocumentos />} />
        <Route path="/conta" element={<Conta />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
        <Route path="/admin" element={<Suspense fallback={<LazyFallback />}><AdminRoute><Admin /></AdminRoute></Suspense>} />
        <Route path="/localizapreve" element={<Suspense fallback={<LazyFallback />}><AdminRoute><LocalizaPreve /></AdminRoute></Suspense>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </PageTransition>
);

const IdleTimeoutWrapper = () => {
  const { showWarning, continueSession, logout } = useIdleTimeout();
  return (
    <SessionExpiryModal
      open={showWarning}
      onContinue={continueSession}
      onLogout={logout}
    />
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <IdleTimeoutWrapper />
        <AppRoutes />
        <AdminBadge />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
